import { Workbook } from "exceljs";
import { formatAccountDateTime } from "../accountDate";

export interface CoverSheetData {
	programName: string;
	exportedAt: Date;
	/**
	 * SIMS-9052 — the account's date format and IANA zone, for "Exported at".
	 *
	 * That row used to print `toISOString()`, i.e. UTC, so a reader in the
	 * account's own zone saw a stamp that matched neither their clock nor the
	 * dates in the sheets beside it — those already resolve through the account
	 * zone (see `utils/accountDate.ts`).
	 *
	 * Optional because the other two workbooks that share this cover do not
	 * thread a zone yet; without `dateFormat` the row keeps its ISO shape rather
	 * than silently falling back to the BROWSER's zone, which is the drift
	 * `accountDate.ts` exists to prevent.
	 */
	dateFormat?: string;
	timeZone?: string;
	startDate?: string;
	endDate?: string;
	orgUnitFilter?: string;
	researcherFilter?: string;
	/**
	 * SIMS-8903 — every applied filter as [label, value], using the SAME labels
	 * the screen shows.
	 *
	 * The legacy fields below cannot express this report's filter set: they cover
	 * only dates, org unit and "researcher", so a Payer / Group label / CIN /
	 * Provider selection was simply absent from the cover, and an unpopulated
	 * `researcherFilter` printed "All" — declaring the export covered every
	 * provider when it covered one. A wrong value is worse than a missing row:
	 * an .xlsx travels outside the app, where the reader cannot check the real
	 * filter. When this is supplied it REPLACES the legacy rows.
	 */
	filters?: Array<[string, string]>;
}

export const addCoverSheet = (
	workbook: Workbook,
	data: CoverSheetData,
): void => {
	const sheet = workbook.addWorksheet("Cover");
	sheet.columns = [
		{ key: "label", width: 26 },
		{ key: "value", width: 60 },
	];

	// Program stays first and comes from `programName` — the caller builds it
	// (for Visit Detail, the full comma-separated list of selected programs), so
	// the "Program" pair is dropped to avoid printing it twice.
	const rows: Array<[string, string]> = [
		["Program", data.programName],
		[
			"Exported at",
			data.dateFormat
				? formatAccountDateTime(data.exportedAt, data.dateFormat, data.timeZone)
				: data.exportedAt.toISOString(),
		],
	];
	if (data.filters?.length) {
		rows.push(...data.filters.filter(([label]) => label !== "Program"));
	} else {
		rows.push(
			["Start date", data.startDate ?? "All time"],
			["End date", data.endDate ?? "All time"],
			["Org unit filter", data.orgUnitFilter ?? "All"],
			["Researcher filter", data.researcherFilter ?? "All"],
		);
	}

	for (const [label, value] of rows) {
		const row = sheet.addRow({ label, value });
		row.getCell(1).font = { bold: true };
	}
};
